import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type FC, useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";

import { type AppDispatch, type RootState } from "../../../../../../Application/globaltypes/redux";
import {
  isUserDeleted,
  formatUserName,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
} from "../../../../../Common/utils/performanceUtils";
import { selectSendDetailsData, selectSendDetailsState } from "../../../state/slices/emailPerformanceSlice";
import { EmailSendDetailStatusToColor, EmailSendDetailStatusToIcon, EmailSendDetailStatusToString } from "./constants";

import "../../../../../Common/utils/performanceSCSSUtils.scss";
import "../emailPerformance.scss";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RouteNames } from "enums";
import { type ColumnOption } from "interfaces/columnOptions";
import dateTimeUtils from "utils/dateTimeUtils";
import { type EmailSendDetailEntity } from "../types";

import styles from "./statuses.module.scss";
import { fetchEmailSendDetails } from "../../../state/thunks/emailPerformanceThunk";
import { MoboLink } from "components/MoboLink/MoboLink";
import { type AccountType } from "../../../../../../../interfaces/Account";

export interface Props extends PropsFromRedux {
  emailId: number;
  sendSessionId: number;
  flowId?: number;
  dateFilter: PerformanceWithAccountFilter & { type: AccountType };
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

const getColumnOptions = (
  filter: PerformanceWithAccountFilter,
  accountId: number,
  includeAccounts?: boolean,
  childAccountId?: number,
  hasCreateAccountsAddOn?: boolean,
): ColumnOption<EmailSendDetailEntity>[] => [
  {
    name: "Name",
    width: 4,
    isSortable: false,
    render: (entity) => {
      const formattedNameDisplay = formatUserName(entity.UserFullName);
      const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
      const includeAccountsActive = includeAccounts ?? (!!filter.accounts?.length && !filter.showCustomers);
      const shouldShowLink =
        !filter.showCustomers &&
        !isUserDeleted(entity.UserFullName) &&
        (!includeAccountsActive || childAccountEnabled || !childAccountId);
      const userName = (
        <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
      );
      const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
        entity.AccountId,
        accountId,
        childAccountId,
        hasCreateAccountsAddOn,
      );
      const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
        childAccountEnabled,
        isUserFromCurrentAccount,
        childAccountId,
        fallbackAccountId: entity.AccountId,
      });
      return (
        <div className="emailListClamp">
          {shouldShowLink ? (
            <MoboLink
              to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
              accountId={linkAccountId}
              target={linkTarget}
              className={styles.linkButton}
            >
              {userName}
            </MoboLink>
          ) : (
            userName
          )}
        </div>
      );
    },
  },
  {
    name: "Email",
    width: 3,
    isSortable: false,
    render: (entity) => (
      <div className="emailListClamp">
        <Tooltip target={<TextTruncate>{entity.UserEmail}</TextTruncate>} content={entity.UserEmail} />
      </div>
    ),
  },
  {
    name: "Send date",
    width: 3,
    isSortable: false,
    render: (entity) => dateTimeUtils.formatWithValidation(entity.SendDate),
  },
  {
    name: "Open date",
    width: 3,
    isSortable: false,
    render: (entity) => dateTimeUtils.formatWithValidation(entity.OpenDate),
  },
  {
    name: "Click date",
    width: 3,
    isSortable: false,
    render: (entity) => dateTimeUtils.formatWithValidation(entity.ClickDate),
  },
  {
    name: "Status",
    width: 3,
    isSortable: false,
    render: (entity) => (
      <div className="emailSendStatusIcon">
        <FontAwesomeIcon
          icon={EmailSendDetailStatusToIcon[entity.Status]}
          className={`${styles[EmailSendDetailStatusToColor[entity.Status]]}`}
        />
        &nbsp;
        {EmailSendDetailStatusToString[entity.Status]}
      </div>
    ),
  },
];

export const EmailPerformanceDrilldown: FC<Props> = ({
  emailId,
  sendSessionId,
  flowId,
  dateFilter,
  sendDetailsState,
  sendDetailsData,
  setHasExportData,
  fetchEmailSendDetails,
  accountId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.EmailReport);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: sendDetailsState.status === RStatus.Got && !!sendDetailsData.length,
      tooltipContent: "No data available for export. This email send session contains no data.",
    });
  }, [sendDetailsData, setHasExportData, sendDetailsState.status]);

  useEffect(() => {
    if (reportEnabled) {
      fetchEmailSendDetails(emailId, sendSessionId, { ...dateFilter, flowId });
    }
  }, [fetchEmailSendDetails, emailId, dateFilter, flowId, sendSessionId, reportEnabled]);

  if (!reportEnabled) return null;

  return (
    <div>
      <RequestStatusRenderer state={sendDetailsState}>
        <GenericPerformanceList
          rows={sendDetailsData}
          columns={getColumnOptions(dateFilter, accountId, includeAccounts, childAccountId, hasCreateAccountsAddOn)}
          title="People"
          filter={dateFilter}
        />
      </RequestStatusRenderer>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    sendDetailsState: selectSendDetailsState(state),
    sendDetailsData: selectSendDetailsData(state),
    accountId: state.userProfile.accountId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    fetchEmailSendDetails: bindAction(fetchEmailSendDetails, dispatch),
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EmailPerformanceDrilldown);
export default ConnectedComponent;
